import React, { useContext, useMemo } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ThemeContext } from "../context/ThemeContext";

const makeStyles = (themeColors: any): Record<string, React.CSSProperties> => ({
  page: {
    minHeight: "100vh",
    padding: "44px 18px",
    background: themeColors.bg,
    color: themeColors.text,
    fontFamily:
      "Inter, system-ui, -apple-system, BlinkMacSystemFont, sans-serif",
    boxSizing: "border-box",
  },

  shell: {
    maxWidth: 1100,
    margin: "0 auto",
  },

  hero: {
    border: `1px solid ${themeColors.border}`,
    background: themeColors.panel,
    borderRadius: 30,
    padding: "34px clamp(20px, 4vw, 48px)",
    boxShadow: themeColors.glow,
    backdropFilter: "blur(18px)",
    marginBottom: 22,
  },

  eyebrow: {
    color: themeColors.accent2,
    letterSpacing: 2,
    textTransform: "uppercase",
    fontSize: 12,
    fontWeight: 900,
    marginBottom: 10,
  },

  title: {
    fontSize: "clamp(34px, 5vw, 56px)",
    margin: "0 0 12px",
    color: themeColors.text,
    lineHeight: 1.02,
  },

  intro: {
    color: themeColors.mutedText,
    fontSize: 17,
    lineHeight: 1.75,
    maxWidth: 900,
    margin: 0,
  },

  grid: {
    display: "grid",
    gridTemplateColumns:
      "repeat(auto-fit, minmax(280px, 1fr))",
    gap: 18,
    alignItems: "start",
  },

  card: {
    border: `1px solid ${themeColors.border}`,
    background: themeColors.cardBg,
    borderRadius: 24,
    padding: 22,
    boxShadow: "0 14px 42px rgba(0,0,0,0.28)",
    minWidth: 0,
    alignSelf: "start",
    height: "fit-content",
  },

  wideCard: {
    border: `1px solid ${themeColors.border}`,
    background: themeColors.panel,
    borderRadius: 26,
    padding: 24,
    boxShadow: themeColors.glow,
    marginBottom: 18,
  },

  h2: {
    color: themeColors.accent,
    fontSize: 21,
    margin: "0 0 10px",
  },

  p: {
    color: themeColors.text,
    fontSize: 15.5,
    lineHeight: 1.72,
    margin: "8px 0",
  },

  list: {
    color: themeColors.text,
    lineHeight: 1.75,
    paddingLeft: 20,
    margin: "10px 0 0",
  },

  nav: {
    display: "flex",
    flexWrap: "wrap",
    gap: 12,
    marginTop: 24,
  },

  link: {
    color: themeColors.accent2,
    textDecoration: "none",
    fontWeight: 850,
    border: `1px solid ${themeColors.border}`,
    background: themeColors.buttonBg,
    padding: "10px 14px",
    borderRadius: 999,
  },

  badgeRow: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: 10,
    marginTop: 14,
  },

  badge: {
    display: "inline-flex",
    alignItems: "center",
    gap: 7,
    margin: 0,
    padding: "7px 10px",
    borderRadius: 999,
    border: `1px solid ${themeColors.border}`,
    background: themeColors.buttonBg,
    color: themeColors.accent,
    fontSize: 12,
    fontWeight: 800,
  },

  note: {
    marginTop: 13,
    padding: "12px 14px",
    borderRadius: 15,
    border: `1px solid ${themeColors.border}`,
    background: themeColors.cardBg,
    color: themeColors.mutedText,
    fontSize: 13,
    lineHeight: 1.6,
  },

  footer: {
    marginTop: 28,
    color: themeColors.mutedText,
    fontSize: 13,
    lineHeight: 1.65,
    textAlign: "center",
  },
});

export default function Info() {
  const { themeColors } = useContext(ThemeContext);
  const styles = useMemo(() => makeStyles(themeColors), [themeColors]);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const isFirstWelcome = searchParams.get("welcome") === "1";

  const meetAva = () => {
    try {
      localStorage.setItem("ava_onboarding_complete", "1");
      localStorage.setItem("ava_orb_menu_discovered", "0");
    } catch {}

    navigate("/dashboard", { replace: true });
  };

  return (
    <main style={styles.page}>
      <div style={styles.shell}>
        {isFirstWelcome && (
          <section
            style={{
              ...styles.hero,
              border: `1px solid ${themeColors.accent}`,
            }}
          >
            <div style={styles.eyebrow}>WELCOME TO AVAAIO™</div>

            <h1 style={styles.title}>Start Here</h1>

            <p style={styles.intro}>
              Ava is your AI assistant. Speak or type to Ava from the
              Dashboard. On mobile, tap Ava’s glowing Orb to open the
              Quick Menu for Settings, Theme, Profile, System controls,
              and other AVAAIO features.
            </p>

            <p style={{ ...styles.p, marginTop: 18 }}>
              You can return to the Ava Information Center anytime for
              help with voice, vision, memory, Offline Local Mode,
              learning, entertainment, privacy, accounts, and support.
            </p>

            <button
              type="button"
              onClick={meetAva}
              style={{
                marginTop: 20,
                border: `1px solid ${themeColors.border}`,
                background: themeColors.buttonBg,
                color: themeColors.text,
                padding: "12px 18px",
                borderRadius: 999,
                fontWeight: 900,
                fontSize: 15,
                cursor: "pointer",
                boxShadow: themeColors.glow,
              }}
            >
              Meet Ava →
            </button>
          </section>
        )}

        <section style={styles.hero}>
          <div style={styles.eyebrow}>INFO</div>

          <h1 style={styles.title}>
            Ava Information Center
          </h1>

          <p style={styles.intro}>
            Your central guide for the AVAAIO™ platform,
            including account management, memberships,
            privacy, safety, Ava features, Offline Local
            Mode, memory, voice, vision, learning tools,
            games, entertainment, personalization, and
            support. This Information Center is designed
            to keep the AVAAIO™ experience clear and
            consistent across supported web, mobile, and
            future Ava systems.
          </p>

          <nav
            style={styles.nav}
            aria-label="AVAAIO information links"
          >
            <Link style={styles.link} to="/support">
              Support
            </Link>

            <Link style={styles.link} to="/profile">
              Profile
            </Link>

            <Link style={styles.link} to="/account">
              Account
            </Link>

            <Link
              style={styles.link}
              to="/privacy-policy"
            >
              Privacy Policy
            </Link>

            <Link
              style={styles.link}
              to="/terms-and-conditions"
            >
              Terms
            </Link>

            <Link
              style={styles.link}
              to="/ai-disclaimer"
            >
              AI Disclaimer
            </Link>
          </nav>
        </section>

        <section style={styles.wideCard}>
          <h2 style={styles.h2}>About Ava</h2>

          <p style={styles.p}>
            Ava is the intelligent assistant experience
            inside the AVAAIO™ platform. Ava brings
            together AI assistance, account features,
            supported memory and personalization, voice
            interaction, vision capabilities, education
            tools, games, entertainment, settings, and
            supported connected and local experiences
            within one unified environment.
          </p>

          <p style={styles.p}>
            Capabilities may vary depending on device,
            connection mode, permissions, membership,
            region, operating environment, and
            availability of supported services.
          </p>

          <div style={styles.badgeRow}>
            <span style={styles.badge}>
              AVAAIO™ Assistant
            </span>

            <span style={styles.badge}>
              Connected + Local Experiences
            </span>
          </div>
        </section>

        <section style={styles.grid}>
          <article style={styles.card}>
            <h2 style={styles.h2}>
              What Ava Can Do
            </h2>

            <ul style={styles.list}>
              <li>
                Answer questions and assist with
                everyday tasks.
              </li>

              <li>
                Remember supported preferences when
                memory is enabled.
              </li>

              <li>
                Support learning experiences such as
                Tutor, Math Board, and Flashcards.
              </li>

              <li>
                Provide interactive games including
                Chess and Checkers.
              </li>

              <li>
                Support voice interaction where the
                required device or service capabilities
                are available.
              </li>

              <li>
                Help with account guidance, settings,
                and personalization.
              </li>

              <li>
                Use supported connected tools when
                internet services are available.
              </li>
            </ul>
          </article>

          <article style={styles.card}>
            <h2 style={styles.h2}>
              Offline Local Mode
            </h2>

            <p style={styles.p}>
              Offline Local Mode is AVAAIO’s protected
              local operating mode for supported
              features when supported internet or
              cloud services are unavailable, or when
              a verified user intentionally uses Local
              Mode.
            </p>

            <p style={styles.p}>
              Supported local capabilities can include
              locally available assistant functions,
              typed interaction, locally available
              interface resources, and supported games
              such as Chess and Checkers.
            </p>

            <p style={styles.p}>
              Features that require live internet
              information, cloud AI providers, account
              synchronization, web research, live
              weather, billing services, online
              entertainment, or other remote systems
              may be unavailable or limited until the
              connection returns.
            </p>

            <div style={styles.note}>
              Offline Local Mode does not grant
              administrative authority. Administrative
              functions that require AVAAIO’s connected
              services remain protected and may require
              an internet connection.
            </div>
          </article>

          <article style={styles.card}>
            <h2 style={styles.h2}>
              Account &amp; Login Help
            </h2>

            <p style={styles.p}>
              You can manage your AVAAIO™ account from
              the Profile and Account pages. If you have
              trouble signing in, use the available
              password-reset, email-verification,
              reactivation, or support options.
            </p>

            <p style={styles.p}>
              Account status, membership information,
              preferences, and supported account
              settings are available through your
              AVAAIO account.
            </p>

            <p style={styles.p}>
              Some account-management functions require
              an internet connection and may not be
              available during Offline Local Mode.
            </p>
          </article>

          <article style={styles.card}>
            <h2 style={styles.h2}>
              Account Deletion &amp; Recovery
            </h2>

            <p style={styles.p}>
              When AVAAIO™ account deletion is
              initiated, the account may first enter a
              temporary deactivated recovery state.
              During this supported recovery period,
              the account may be eligible for
              restoration for up to 30 days.
            </p>

            <p style={styles.p}>
              Reactivation may require confirmation
              through the account’s registered email
              address. If you request a reactivation
              email and do not see it, check spam,
              junk, and promotions folders.
            </p>

            <p style={styles.p}>
              After the applicable recovery period
              expires and permanent deletion is
              completed, the account may no longer be
              recoverable.
            </p>

            <p style={styles.p}>
              Certain records may be retained where
              reasonably necessary for billing,
              security, fraud prevention, dispute
              resolution, legal compliance, or
              enforcement.
            </p>

            <div style={styles.note}>
              Deleting an AVAAIO account does not
              automatically cancel a subscription
              purchased through Apple. Apple
              subscriptions must be managed separately
              through the user’s Apple Account
              subscription settings.
            </div>
          </article>

          <article style={styles.card}>
            <h2 style={styles.h2}>
              Membership &amp; Plans
            </h2>

            <p style={styles.p}>
              AVAAIO™ provides Free and Pro access.
            </p>

            <p style={styles.p}>
              Free access includes Ava’s core
              experience, standard usage limits,
              account and settings features, support,
              basic personalization, supported games,
              and supported learning features.
            </p>

            <p style={styles.p}>
              AVAAIO Pro provides expanded access,
              including higher supported usage limits
              and premium capabilities such as
              enhanced memory, advanced Voice and
              Vision features, multilingual or
              connected tools, and other Pro features
              where supported.
            </p>

            <p style={styles.p}>
              AVAAIO Pro is currently offered as a
              monthly subscription where available.
              Current pricing and billing terms are
              shown before purchase.
            </p>
          </article>

          <article style={styles.card}>
            <h2 style={styles.h2}>
              Subscriptions, Cancellations &amp;
              Refunds
            </h2>

            <p style={styles.p}>
              AVAAIO™ subscriptions are billed for the
              selected subscription period according
              to the applicable billing provider’s
              terms.
            </p>

            <p style={styles.p}>
              If a subscription is canceled, paid
              access generally remains available
              through the end of the current paid
              billing period unless otherwise required
              by the billing provider or applicable
              law.
            </p>

            <p style={styles.p}>
              For purchases made through Apple,
              renewal, cancellation, eligible purchase
              restoration, and eligible refund
              requests are handled through the user’s
              Apple Account subscription settings or
              Apple Support.
            </p>

            <div style={styles.note}>
              AVAAIO account deletion and Apple
              subscription cancellation are separate
              actions.
            </div>
          </article>

          <article style={styles.card}>
            <h2 style={styles.h2}>Ava Voice</h2>

            <p style={styles.p}>
              Ava Voice supports speech-based
              interaction with Ava, including supported
              microphone input, spoken responses, mute
              controls, and conversational voice
              experiences.
            </p>

            <p style={styles.p}>
              Microphone access is used only when the
              user enables or activates a feature that
              requires it and grants the necessary
              device permission.
            </p>

            <p style={styles.p}>
              Voice capabilities may vary by device,
              operating system, browser or app
              permissions, selected language,
              connection mode, region, membership, and
              availability of supported local or
              connected voice services.
            </p>

            <p style={styles.p}>
              During Offline Local Mode, cloud-based
              voice services may be unavailable.
              Supported device-based or locally
              available voice capabilities may remain
              available where supported.
            </p>
          </article>

          <article style={styles.card}>
            <h2 style={styles.h2}>Ava Vision</h2>

            <p style={styles.p}>
              Ava Vision is Ava’s optional visual
              interaction system for supported
              camera-assisted and immersive
              experiences.
            </p>

            <p style={styles.p}>
              Camera access is used only when the user
              enables a feature that requires it and
              grants the necessary device permission.
            </p>

            <p style={styles.p}>
              Vision capabilities may vary by device,
              connection mode, membership, permissions,
              and availability of the required local
              or connected processing systems.
            </p>

            <p style={styles.p}>
              Some advanced Vision features may require
              AVAAIO Pro or an internet connection,
              while supported local visual functions
              may remain available when the required
              resources exist locally.
            </p>
          </article>

          <article style={styles.card}>
            <h2 style={styles.h2}>
              Memory &amp; Personalization
            </h2>

            <p style={styles.p}>
              Ava’s memory and personalization systems
              can use supported information to improve
              continuity, preferences, interaction
              style, and useful context when memory is
              enabled.
            </p>

            <p style={styles.p}>
              Users remain in control of available
              memory and personalization settings.
            </p>

            <p style={styles.p}>
              Memory behavior can differ between
              connected and Offline Local Mode.
              Information that depends on cloud
              synchronization or unavailable services
              may not be accessible while offline.
            </p>
          </article>

          <article style={styles.card}>
            <h2 style={styles.h2}>
              Games &amp; Learning
            </h2>

            <p style={styles.p}>
              AVAAIO™ includes interactive experiences
              such as Chess, Checkers, Tutor, Math
              Board, Flashcards, and other education
              features designed to support learning,
              practice, focus, and entertainment.
            </p>

            <p style={styles.p}>
              Supported locally operated features,
              including available games and learning
              tools, can continue functioning in
              Offline Local Mode when their required
              resources are available locally.
            </p>

            <p style={styles.p}>
              Features requiring Ava’s connected
              intelligence, live information, cloud
              synchronization, or other online
              services may have reduced functionality
              while offline.
            </p>
          </article>

          <article style={styles.card}>
            <h2 style={styles.h2}>
              Entertainment
            </h2>

            <p style={styles.p}>
              AVAAIO™ provides supported entertainment
              and interactive media experiences through
              curated platform features.
            </p>

            <p style={styles.p}>
              Some entertainment content depends on
              third-party or internet-connected
              services and therefore may not be
              available during Offline Local Mode.
            </p>

            <p style={styles.p}>
              Content availability can vary by
              platform, region, connection, provider
              availability, and membership level.
            </p>
          </article>

          <article style={styles.card}>
            <h2 style={styles.h2}>
              Privacy &amp; Safety
            </h2>

            <p style={styles.p}>
              AVAAIO™ is designed with account
              controls, user permissions, privacy
              settings, and safeguards for supported
              AI, memory, voice, vision, uploads, and
              platform functionality.
            </p>

            <p style={styles.p}>
              AVAAIO™ does not sell users’ personal
              information.
            </p>

            <p style={styles.p}>
              Certain connected features may involve
              third-party or external services when
              those services are used. Offline Local
              Mode is designed to keep supported local
              operation separate from cloud-dependent
              functionality while that mode is active.
            </p>

            <p style={styles.p}>
              Review the Privacy Policy, Terms and
              Conditions, and AI Disclaimer for
              additional information about data
              processing, memory, voice, vision,
              uploads, third-party services, AI
              limitations, security, and user
              responsibilities.
            </p>
          </article>

          <article style={styles.card}>
            <h2 style={styles.h2}>
              Support &amp; Contact
            </h2>

            <p style={styles.p}>
              For assistance with account access,
              billing, subscriptions, bugs, technical
              issues, feature questions, or general
              support, visit the Support page.
            </p>

            <p style={styles.p}>
              You can also contact the Ava team at{" "}
              <a
                href="mailto:support@avaaio.com"
                style={{
                  color: themeColors.accent2,
                  fontWeight: 800,
                }}
              >
                support@avaaio.com
              </a>
              .
            </p>

            <p style={styles.p}>
              Internet connectivity is required to
              submit online support requests or access
              connected account-management services.
            </p>
          </article>
        </section>

        <footer style={styles.footer}>
          <div>
            © 2026 American Globalo, LLC. All rights
            reserved.
          </div>

          <div style={{ marginTop: 6 }}>
            AVAAIO™ is owned and operated by American
            Globalo, LLC. Ava is the AI assistant
            experience within the AVAAIO™ platform.
          </div>
        </footer>
      </div>
    </main>
  );
}
